<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import { isPhoneE164OrEmpty, isNumber } from 'shared/helpers/Validators';
import InboxHealthAPI from 'dashboard/api/inboxHealth';

import NextButton from 'dashboard/components-next/button/Button.vue';

// SUBIR EM LOTE (pedido do Kairo 02/07/2026): colunas esperadas no CSV ->
// campo interno. Cabecalho comparado sem acento/caixa.
const BULK_COLUMNS = {
  'nome da caixa de entrada': 'name',
  'numero de telefone': 'phone',
  'id do numero de telefone': 'phoneNumberId',
  'id da conta do whatsapp business': 'businessAccountId',
  'chave da api': 'apiKey',
  'id do aplicativo': 'appId',
  'chave secreta do aplicativo': 'appSecret',
};

const normalizeHeader = value =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export default {
  components: {
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      phoneNumber: '',
      apiKey: '',
      phoneNumberId: '',
      businessAccountId: '',
      appId: '',
      appSecret: '',
      showBulkModal: false,
      bulkRows: [],
      bulkRunning: false,
      bulkDone: false,
      bulkHeaderError: '',
      bulkFileName: '',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
      currentUser: 'getCurrentUser',
      inboxesList: 'inboxes/getInboxes',
    }),
    // SUBIR EM LOTE: somente o super admin (id 1) ve o botao.
    isSuperAdmin() {
      return this.currentUser?.id === 1;
    },
    bulkOkCount() {
      return this.bulkRows.filter(r => r.status === 'ok').length;
    },
    bulkFailCount() {
      return this.bulkRows.filter(r => r.status === 'erro').length;
    },
  },
  validations: {
    inboxName: { required },
    phoneNumber: { required, isPhoneE164OrEmpty },
    apiKey: { required },
    phoneNumberId: { required, isNumber },
    businessAccountId: { required, isNumber },
    appId: { required, isNumber },
    appSecret: { required },
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const whatsappChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.inboxName?.trim(),
            channel: {
              type: 'whatsapp',
              phone_number: this.phoneNumber,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: this.apiKey,
                phone_number_id: this.phoneNumberId,
                business_account_id: this.businessAccountId,
                app_id: this.appId,
                app_secret: this.appSecret,
              },
            },
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: {
            page: 'new',
            inbox_id: whatsappChannel.id,
          },
        });
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
        );
      }
    },
    openBulkModal() {
      this.showBulkModal = true;
    },
    closeBulkModal() {
      if (this.bulkRunning) return;
      this.showBulkModal = false;
      this.bulkRows = [];
      this.bulkDone = false;
      this.bulkHeaderError = '';
      this.bulkFileName = '';
    },
    // Parser CSV minimo: BOM, aspas e separador , ou ; (Excel BR).
    parseBulkCsv(text) {
      const clean = text.replace(/^\uFEFF/, '');
      const lines = clean
        .split(/\r\n|\n|\r/)
        .filter(line => line.trim() !== '');
      if (!lines.length) return { header: [], rows: [] };
      const first = lines[0];
      const delimiter =
        (first.match(/;/g) || []).length > (first.match(/,/g) || []).length
          ? ';'
          : ',';
      const parseLine = line => {
        const out = [];
        let cur = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i += 1) {
          const ch = line[i];
          if (inQuotes) {
            if (ch === '"') {
              if (line[i + 1] === '"') {
                cur += '"';
                i += 1;
              } else {
                inQuotes = false;
              }
            } else {
              cur += ch;
            }
          } else if (ch === '"') {
            inQuotes = true;
          } else if (ch === delimiter) {
            out.push(cur);
            cur = '';
          } else {
            cur += ch;
          }
        }
        out.push(cur);
        return out.map(v => v.trim());
      };
      return {
        header: parseLine(lines[0]),
        rows: lines.slice(1).map(parseLine),
      };
    },
    onBulkFileChange(event) {
      const file = event.target.files?.[0];
      this.bulkRows = [];
      this.bulkDone = false;
      this.bulkHeaderError = '';
      this.bulkFileName = file?.name || '';
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const { header, rows } = this.parseBulkCsv(String(reader.result));
        const indexByField = {};
        header.forEach((col, idx) => {
          const field = BULK_COLUMNS[normalizeHeader(col)];
          if (field && indexByField[field] === undefined) {
            indexByField[field] = idx;
          }
        });
        const missing = Object.entries(BULK_COLUMNS)
          .filter(([, field]) => indexByField[field] === undefined)
          .map(([label]) => label);
        if (missing.length) {
          this.bulkHeaderError = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.BULK.INVALID_HEADER',
            { columns: missing.join(', ') }
          );
          return;
        }
        if (!rows.length) {
          this.bulkHeaderError = this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.BULK.NO_ROWS'
          );
          return;
        }
        this.bulkRows = rows.map(cols => {
          const value = field => (cols[indexByField[field]] || '').trim();
          let phone = value('phone').replace(/[^\d+]/g, '');
          if (phone && !phone.startsWith('+')) phone = `+${phone}`;
          return {
            name: value('name'),
            phone,
            phoneNumberId: value('phoneNumberId'),
            businessAccountId: value('businessAccountId'),
            apiKey: value('apiKey'),
            appId: value('appId'),
            appSecret: value('appSecret'),
            status: 'pendente',
            error: '',
            existingName: '',
            inboxId: null,
          };
        });
      };
      reader.readAsText(file);
      // permite re-selecionar o mesmo arquivo
      event.target.value = '';
    },
    // Processa UMA linha por vez: cria a caixa (sem etapa de agentes) e
    // cadastra o webhook (App + WABA). Erro -> pula pra proxima.
    async startBulk() {
      if (this.bulkRunning || !this.bulkRows.length) return;
      this.bulkRunning = true;
      this.bulkDone = false;
      // Lista atualizada pra detectar numero ja cadastrado (caixa existente).
      await this.$store.dispatch('inboxes/get');
      for (const row of this.bulkRows) {
        if (row.status === 'ok') continue; // re-execucao apos erro parcial
        const existing = this.inboxesList.find(
          i => i.phone_number === row.phone
        );
        if (existing) {
          row.existingName = existing.name;
          row.status = 'erro';
          row.error = this.$t('INBOX_MGMT.ADD.WHATSAPP.BULK.EXISTS', {
            name: existing.name,
          });
          continue;
        }
        try {
          row.status = 'criando';
          row.error = '';
          const inbox = await this.$store.dispatch('inboxes/createChannel', {
            name: row.name,
            channel: {
              type: 'whatsapp',
              phone_number: row.phone,
              provider: 'whatsapp_cloud',
              provider_config: {
                api_key: row.apiKey,
                phone_number_id: row.phoneNumberId,
                business_account_id: row.businessAccountId,
                app_id: row.appId,
                app_secret: row.appSecret,
              },
            },
          });
          row.inboxId = inbox.id;
          row.status = 'webhook';
          await InboxHealthAPI.registerWebhook(inbox.id);
          row.status = 'ok';
        } catch (error) {
          row.status = 'erro';
          row.error =
            error?.response?.data?.error ||
            error?.message ||
            this.$t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
        }
      }
      this.bulkRunning = false;
      this.bulkDone = true;
    },
    // Baixa o resultado do lote em CSV (Caixa de entrada; Telefone; Status).
    downloadBulkResult() {
      const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const linhas = [
        ['Caixa de entrada', 'Caixa existente', 'Telefone', 'Status'],
        ...this.bulkRows.map(row => [
          row.name,
          row.existingName,
          row.phone,
          row.error ? `${this.bulkStatusLabel(row)}: ${row.error}` : this.bulkStatusLabel(row),
        ]),
      ];
      const csv = `\ufeff${linhas.map(cols => cols.map(esc).join(';')).join('\r\n')}`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'resultado-lote-caixas.csv';
      link.click();
      URL.revokeObjectURL(link.href);
    },
    bulkStatusLabel(row) {
      const map = {
        pendente: 'ROW_PENDING',
        criando: 'ROW_CREATING',
        webhook: 'ROW_WEBHOOK',
        ok: 'ROW_OK',
        erro: 'ROW_ERROR',
      };
      return this.$t(`INBOX_MGMT.ADD.WHATSAPP.BULK.${map[row.status]}`);
    },
    bulkStatusClass(row) {
      return {
        pendente: 'text-n-slate-11',
        criando: 'text-n-amber-11',
        webhook: 'text-n-amber-11',
        ok: 'text-n-teal-11',
        erro: 'text-n-ruby-11',
      }[row.status];
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full">
    <div v-if="isSuperAdmin" class="flex justify-end w-full mb-2">
      <NextButton
        type="button"
        solid
        teal
        :label="$t('INBOX_MGMT.ADD.WHATSAPP.BULK.BUTTON')"
        @click="openBulkModal"
      />
    </div>

    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.PLACEHOLDER')"
          @blur="v$.inboxName.$touch"
        />
        <span v-if="v$.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.INBOX_NAME.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumber.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.LABEL') }}
        <input
          v-model="phoneNumber"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.PLACEHOLDER')"
          @blur="v$.phoneNumber.$touch"
        />
        <span v-if="v$.phoneNumber.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.phoneNumberId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.LABEL') }}
        </span>
        <input
          v-model="phoneNumberId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.PLACEHOLDER')
          "
          @blur="v$.phoneNumberId.$touch"
        />
        <span v-if="v$.phoneNumberId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PHONE_NUMBER_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.businessAccountId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.LABEL') }}
        </span>
        <input
          v-model="businessAccountId"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.PLACEHOLDER')
          "
          @blur="v$.businessAccountId.$touch"
        />
        <span v-if="v$.businessAccountId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BUSINESS_ACCOUNT_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.apiKey.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.LABEL') }}
        </span>
        <input
          v-model="apiKey"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.PLACEHOLDER')"
          @blur="v$.apiKey.$touch"
        />
        <span v-if="v$.apiKey.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.API_KEY.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.appId.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.LABEL') }}
        </span>
        <input
          v-model="appId"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.PLACEHOLDER')"
          @blur="v$.appId.$touch"
        />
        <span v-if="v$.appId.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_ID.ERROR') }}
        </span>
      </label>
    </div>

    <div class="flex-shrink-0 flex-grow-0">
      <label :class="{ error: v$.appSecret.$error }">
        <span>
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_SECRET.LABEL') }}
        </span>
        <input
          v-model="appSecret"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.WHATSAPP.APP_SECRET.PLACEHOLDER')"
          @blur="v$.appSecret.$touch"
        />
        <span v-if="v$.appSecret.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.APP_SECRET.ERROR') }}
        </span>
      </label>
    </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.WHATSAPP.SUBMIT_BUTTON')"
        />
      </div>
    </form>

    <!-- Modal SUBIR EM LOTE -->
    <div
      v-if="showBulkModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="closeBulkModal"
    >
      <div
        class="flex flex-col w-full max-w-2xl p-6 mx-4 rounded-xl shadow-2xl bg-n-solid-1 border border-n-weak max-h-[85vh]"
      >
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BULK.TITLE') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.BULK.DESCRIPTION') }}
        </p>

        <label class="mt-4">
          <input
            type="file"
            accept=".csv,text/csv"
            :disabled="bulkRunning"
            @change="onBulkFileChange"
          />
          <span v-if="bulkFileName" class="block mt-1 text-xs text-n-slate-10">
            {{ bulkFileName }}
          </span>
        </label>

        <p v-if="bulkHeaderError" class="mt-2 text-sm text-n-ruby-11">
          {{ bulkHeaderError }}
        </p>

        <div
          v-if="bulkRows.length"
          class="flex-1 mt-4 overflow-y-auto border rounded-lg border-n-weak"
        >
          <table class="w-full text-sm">
            <thead class="sticky top-0 bg-n-solid-2">
              <tr class="text-left text-n-slate-11">
                <th class="px-3 py-2">#</th>
                <th class="px-3 py-2">
                  {{ $t('INBOX_MGMT.ADD.WHATSAPP.BULK.COL_NAME') }}
                </th>
                <th class="px-3 py-2">
                  {{ $t('INBOX_MGMT.ADD.WHATSAPP.BULK.COL_PHONE') }}
                </th>
                <th class="px-3 py-2">
                  {{ $t('INBOX_MGMT.ADD.WHATSAPP.BULK.COL_STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in bulkRows"
                :key="index"
                class="border-t border-n-weak"
              >
                <td class="px-3 py-2 text-n-slate-10">{{ index + 1 }}</td>
                <td class="px-3 py-2 text-n-slate-12">{{ row.name }}</td>
                <td class="px-3 py-2 text-n-slate-11">{{ row.phone }}</td>
                <td class="px-3 py-2 font-medium" :class="bulkStatusClass(row)">
                  {{ bulkStatusLabel(row) }}
                  <span v-if="row.error" class="block text-xs font-normal">
                    {{ row.error }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="bulkDone" class="mt-3 text-sm font-medium text-n-slate-12">
          {{
            $t('INBOX_MGMT.ADD.WHATSAPP.BULK.SUMMARY', {
              ok: bulkOkCount,
              fail: bulkFailCount,
            })
          }}
        </p>

        <div class="flex justify-between gap-3 mt-6">
          <NextButton
            type="button"
            outline
            slate
            :disabled="bulkRunning"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.BULK.CLOSE')"
            @click="closeBulkModal"
          />
          <NextButton
            v-if="bulkDone"
            type="button"
            outline
            teal
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.BULK.DOWNLOAD')"
            @click="downloadBulkResult"
          />
          <NextButton
            type="button"
            solid
            blue
            :is-loading="bulkRunning"
            :disabled="bulkRunning || !bulkRows.length"
            :label="$t('INBOX_MGMT.ADD.WHATSAPP.BULK.START')"
            @click="startBulk"
          />
        </div>
      </div>
    </div>
  </div>
</template>
